import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SystemEmailService,
  SystemEmailTemplate,
  SYSTEM_EMAIL_TEMPLATE_LABELS,
} from "@/services/communication/SystemEmailService";

const TEMPLATES = Object.entries(SYSTEM_EMAIL_TEMPLATE_LABELS) as [
  SystemEmailTemplate,
  string,
][];

const SystemEmailsPage = () => {
  const { userProfile } = useAuth();
  const [selectedTemplate, setSelectedTemplate] =
    useState<SystemEmailTemplate>("welcome_business");
  const [recipient, setRecipient] = useState<string>(
    (userProfile as any)?.email ?? "",
  );

  const { data: previewHtml, isFetching } = useQuery({
    queryKey: ["system-email-preview", selectedTemplate],
    queryFn: () => SystemEmailService.preview(selectedTemplate),
    staleTime: Infinity,
  });

  const sendTestMutation = useMutation({
    mutationFn: () => SystemEmailService.sendTest(selectedTemplate, recipient),
    onSuccess: () =>
      toast.success(`Test email sent to ${recipient}`),
    onError: (err: Error) =>
      toast.error(err.message ?? "Failed to send test email"),
  });

  const handleSendTest = () => {
    if (!recipient.trim()) {
      toast.error("Enter a recipient email address");
      return;
    }
    sendTestMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <div className="w-full">
          <PageHeader
            title="System Emails"
            genieIcon="mail"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Controls */}
            <div className="lg:col-span-1 space-y-5">
              <div className="bg-white border border-[#E5EAF0] rounded-xl p-5 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#222B45]">
                    Template
                  </Label>
                  <Select
                    value={selectedTemplate}
                    onValueChange={(v) =>
                      setSelectedTemplate(v as SystemEmailTemplate)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATES.map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="recipient"
                    className="text-sm font-medium text-[#222B45]"
                  >
                    Send test to
                  </Label>
                  <Input
                    id="recipient"
                    type="email"
                    placeholder="email@example.com"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                  />
                </div>

                <Button
                  onClick={handleSendTest}
                  disabled={sendTestMutation.isPending}
                  className="w-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white hover:opacity-90"
                >
                  {sendTestMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Test Email
                </Button>
              </div>

              <div className="bg-white border border-[#E5EAF0] rounded-xl p-5">
                <p className="text-xs text-[#64748B] leading-relaxed">
                  These are system-generated transactional emails. The preview
                  uses sample data. The test send delivers the real rendered
                  HTML to the address above with a{" "}
                  <span className="font-medium">[Test]</span> subject prefix.
                </p>
              </div>
            </div>

            {/* Preview */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-[#E5EAF0] rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-[#E5EAF0] flex items-center justify-between">
                  <span className="text-sm font-medium text-[#222B45]">
                    Preview — {SYSTEM_EMAIL_TEMPLATE_LABELS[selectedTemplate]}
                  </span>
                  {isFetching && (
                    <Loader2 className="h-4 w-4 animate-spin text-[#4E9CFF]" />
                  )}
                </div>
                {previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    title="Email preview"
                    className="w-full border-0"
                    style={{ height: "780px" }}
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div className="flex items-center justify-center h-[780px]">
                    <Loader2 className="h-6 w-6 animate-spin text-[#4E9CFF]" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemEmailsPage;
